/**
 * AutoCasesTodayWidget
 *
 * Slim dashboard tile showing the number of cases auto-generated by the
 * ED/CBI press-release matcher today. Tenant-scoped via the
 * `GET /api/dashboard/auto-cases-today` endpoint. Click → drill-through to
 * the Cases page filtered by `auto_source=ED_PRESS_AUTO`.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";

const API = `${window.location.origin}/api`;

export function AutoCasesTodayWidget() {
  const [count, setCount] = useState(null);
  const [asOf, setAsOf] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/auto-cases-today`,
        { withCredentials: true });
      setCount(data?.count ?? 0);
      setAsOf(data?.as_of || null);
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (count === null) return null;
  const hot = count > 0;

  return (
    <Link to="/cases?auto_source=ED_PRESS_AUTO&scope=today"
      data-testid="auto-cases-today-widget"
      style={{
        display: "flex", alignItems: "center", gap: 14, textDecoration: "none",
        background: hot
          ? "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(239,68,68,0.06))"
          : "#0d1117",
        border: `1px solid ${hot ? "rgba(249,115,22,0.40)" : "#1e2530"}`,
        borderRadius: 10, padding: "12px 16px", marginBottom: 24,
      }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: hot ? "rgba(249,115,22,0.18)" : "rgba(148,163,184,0.10)",
        border: `1px solid ${hot ? "rgba(249,115,22,0.45)" : "#1e2530"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Sparkles style={{ width: 17, height: 17,
                           color: hot ? "#fb923c" : "#94a3b8" }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: hot ? "#fed7aa" : "#94a3b8", fontSize: 11,
                      fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Auto-cases created today
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
          <span data-testid="auto-cases-count"
            style={{ color: hot ? "#fb923c" : "#e2e8f0", fontSize: 22, fontWeight: 800,
                     fontFamily: "ui-monospace, monospace" }}>{count}</span>
          <span style={{ color: "#64748b", fontSize: 11 }}>
            via ED / CBI press-release matcher
          </span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5,
                    color: hot ? "#fb923c" : "#64748b", fontSize: 12, fontWeight: 600 }}>
        View {hot ? "cases" : "all"}
        <ArrowRight style={{ width: 13, height: 13 }} />
      </div>
      {asOf && (
        <div style={{ color: "#475569", fontSize: 10, marginLeft: 4 }}>
          as of {new Date(asOf).toLocaleTimeString()}
        </div>
      )}
    </Link>
  );
}
